// src/command-registry.ts
function createCommandRegistry(initialCommands = []) {
  const commands = /* @__PURE__ */ new Map();
  for (const command of initialCommands) {
    commands.set(command.id, command);
  }
  const list = () => Array.from(commands.values()).sort(
    (a, b) => a.title.localeCompare(b.title)
  );
  return {
    register(command) {
      if (commands.has(command.id)) {
        throw new Error(`Command already registered: ${command.id}`);
      }
      commands.set(command.id, command);
      return () => {
        commands.delete(command.id);
      };
    },
    list,
    find(query) {
      const available = list();
      const normalizedQuery = normalize(query);
      if (!normalizedQuery) {
        return available;
      }
      return available.filter((command) => {
        const haystack = normalize(
          [
            command.title,
            command.group,
            command.id,
            ...command.keywords ?? []
          ].join(" ")
        );
        return haystack.includes(normalizedQuery) || isSubsequence(normalizedQuery, haystack);
      });
    },
    async run(id) {
      const command = commands.get(id);
      if (!command) {
        throw new Error(`Unknown command: ${id}`);
      }
      if (command.disabled) {
        return;
      }
      await command.handler();
    }
  };
}
function normalize(value) {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}
function isSubsequence(needle, haystack) {
  let index = 0;
  for (const character of haystack) {
    if (character === needle[index]) {
      index += 1;
    }
    if (index === needle.length) {
      return true;
    }
  }
  return false;
}

// src/components.tsx
import Editor from "@monaco-editor/react";
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleDot,
  Code2,
  Command,
  File,
  Folder,
  FolderOpen,
  Info,
  Loader2,
  Search,
  Settings,
  ShieldAlert,
  X
} from "lucide-react";
import {
  createContext as createContext2,
  Fragment,
  useContext as useContext2,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

// src/theme.ts
import { createContext, useContext } from "react";
var AuraThemeContext = createContext({
  mode: "system",
  setMode: () => void 0
});
function useAuraTheme() {
  return useContext(AuraThemeContext);
}

// src/components.tsx
import { jsx, jsxs } from "react/jsx-runtime";
function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}
function AuraIdeAppFrame({
  productName,
  projectName = "No project open",
  commands,
  sidebar,
  main,
  inspector,
  bottomPanel,
  statusBar,
  themeMode = "system",
  onThemeModeChange
}) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mode, setMode] = useState(themeMode);
  useEffect(() => {
    setMode(themeMode);
  }, [themeMode]);
  useEffect(() => {
    const listener = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);
  const themeValue = useMemo(
    () => ({
      mode,
      setMode: (nextMode) => {
        setMode(nextMode);
        onThemeModeChange?.(nextMode);
      }
    }),
    [mode, onThemeModeChange]
  );
  return /* @__PURE__ */ jsx(AuraThemeContext.Provider, { value: themeValue, children: /* @__PURE__ */ jsxs("div", { className: "aura-ide-root", "data-theme": mode, children: [
    /* @__PURE__ */ jsxs("div", { className: "aura-ide-titlebar", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("span", { className: "aura-ide-titlebar__product", children: productName }),
        /* @__PURE__ */ jsx("span", { className: "aura-ide-titlebar__project", children: projectName })
      ] }),
      /* @__PURE__ */ jsxs(
        "button",
        {
          className: "aura-ide-icon-button",
          type: "button",
          "aria-label": "Open command palette",
          onClick: () => setPaletteOpen(true),
          children: [
            /* @__PURE__ */ jsx(Command, { size: 16 }),
            /* @__PURE__ */ jsx("span", { children: "Cmd/Ctrl K" })
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      AuraSplitPane,
      {
        start: /* @__PURE__ */ jsx("aside", { className: "aura-ide-sidebar", children: sidebar }),
        end: /* @__PURE__ */ jsxs("div", { className: "aura-ide-workbench", children: [
          /* @__PURE__ */ jsxs("div", { className: "aura-ide-main-row", children: [
            /* @__PURE__ */ jsx("main", { className: "aura-ide-main", children: main }),
            inspector ? /* @__PURE__ */ jsx("aside", { className: "aura-ide-inspector", children: inspector }) : null
          ] }),
          bottomPanel ? /* @__PURE__ */ jsx("section", { className: "aura-ide-bottom-panel", children: bottomPanel }) : null
        ] }),
        defaultStartSize: 280,
        minStartSize: 220,
        maxStartSize: 420
      }
    ),
    /* @__PURE__ */ jsx("footer", { className: "aura-ide-statusbar", children: statusBar }),
    /* @__PURE__ */ jsx(
      AuraCommandPalette,
      {
        registry: commands,
        open: paletteOpen,
        onOpenChange: setPaletteOpen
      }
    )
  ] }) });
}
function AuraSplitPane({
  start,
  end,
  defaultStartSize = 320,
  minStartSize = 180,
  maxStartSize = 600
}) {
  const [startSize, setStartSize] = useState(defaultStartSize);
  const dragging = useRef(false);
  useEffect(() => {
    const move = (event) => {
      if (!dragging.current) {
        return;
      }
      const next = Math.min(
        maxStartSize,
        Math.max(minStartSize, event.clientX)
      );
      setStartSize(next);
    };
    const up = () => {
      dragging.current = false;
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [maxStartSize, minStartSize]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "aura-split-pane",
      style: { "--aura-pane-start": `${startSize}px` },
      children: [
        /* @__PURE__ */ jsx("div", { className: "aura-split-pane__start", children: start }),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "aura-split-pane__handle",
            role: "separator",
            "aria-orientation": "vertical",
            "aria-valuemin": minStartSize,
            "aria-valuemax": maxStartSize,
            "aria-valuenow": startSize,
            tabIndex: 0,
            onMouseDown: () => {
              dragging.current = true;
            },
            onKeyDown: (event) => {
              if (event.key === "ArrowLeft") {
                setStartSize((value) => Math.max(minStartSize, value - 16));
              }
              if (event.key === "ArrowRight") {
                setStartSize((value) => Math.min(maxStartSize, value + 16));
              }
            }
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "aura-split-pane__end", children: end })
      ]
    }
  );
}
function AuraProjectTree({
  nodes,
  selectedId,
  onSelect
}) {
  return /* @__PURE__ */ jsx("div", { className: "aura-tree", role: "tree", "aria-label": "Project files", children: nodes.map((node) => /* @__PURE__ */ jsx(
    TreeNode,
    {
      node,
      selectedId,
      depth: 0,
      onSelect
    },
    node.id
  )) });
}
function TreeNode({
  node,
  selectedId,
  depth,
  onSelect
}) {
  const [expanded, setExpanded] = useState(node.expanded ?? true);
  const hasChildren = Boolean(node.children?.length);
  const Icon = node.kind === "folder" ? expanded ? FolderOpen : Folder : File;
  const activate = () => {
    if (node.kind === "folder" && hasChildren) {
      setExpanded((value) => !value);
    }
    onSelect?.(node);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        role: "treeitem",
        "aria-expanded": node.kind === "folder" ? expanded : void 0,
        "aria-selected": selectedId === node.id,
        className: "aura-tree-row",
        style: { "--aura-tree-depth": depth },
        onClick: activate,
        children: [
          hasChildren ? expanded ? /* @__PURE__ */ jsx(ChevronDown, { size: 14 }) : /* @__PURE__ */ jsx(ChevronRight, { size: 14 }) : /* @__PURE__ */ jsx("span", { className: "aura-tree-row__spacer" }),
          /* @__PURE__ */ jsx(Icon, { size: 15 }),
          /* @__PURE__ */ jsx("span", { className: "aura-tree-row__name", children: node.name }),
          node.dirty ? /* @__PURE__ */ jsx(
            CircleDot,
            {
              className: "aura-tree-row__dirty",
              size: 10,
              "aria-label": "Dirty"
            }
          ) : null,
          node.conflict ? /* @__PURE__ */ jsx(
            ShieldAlert,
            {
              className: "aura-tree-row__conflict",
              size: 13,
              "aria-label": "Conflict"
            }
          ) : null,
          node.badge ? /* @__PURE__ */ jsx("span", { className: "aura-tree-row__badge", children: node.badge }) : null
        ]
      }
    ),
    expanded && node.children?.map((child) => /* @__PURE__ */ jsx(
      TreeNode,
      {
        node: child,
        selectedId,
        depth: depth + 1,
        onSelect
      },
      child.id
    ))
  ] });
}
function AuraTabbedShell({
  tabs,
  activeTabId,
  onActiveTabChange,
  onCloseTab
}) {
  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];
  return /* @__PURE__ */ jsxs("div", { className: "aura-tabs", children: [
    /* @__PURE__ */ jsx("div", { className: "aura-tabs__bar", role: "tablist", "aria-label": "Open editors", children: tabs.map((tab) => /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        role: "tab",
        "aria-selected": tab.id === activeTab?.id,
        className: "aura-tabs__tab",
        onClick: () => onActiveTabChange(tab.id),
        onMouseDown: (event) => {
          if (event.button === 1) {
            onCloseTab?.(tab.id);
          }
        },
        children: [
          tab.icon,
          /* @__PURE__ */ jsx("span", { children: tab.title }),
          tab.dirty ? /* @__PURE__ */ jsx(
            Circle,
            {
              size: 8,
              fill: "currentColor",
              "aria-label": "Unsaved changes"
            }
          ) : null,
          onCloseTab ? /* @__PURE__ */ jsx(
            "span",
            {
              role: "button",
              tabIndex: 0,
              className: "aura-tabs__close",
              "aria-label": `Close ${tab.title}`,
              onClick: (event) => {
                event.stopPropagation();
                onCloseTab(tab.id);
              },
              onKeyDown: (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onCloseTab(tab.id);
                }
              },
              children: /* @__PURE__ */ jsx(X, { size: 13 })
            }
          ) : null
        ]
      },
      tab.id
    )) }),
    /* @__PURE__ */ jsx("div", { className: "aura-tabs__panel", role: "tabpanel", children: activeTab?.content })
  ] });
}
function AuraMonaco({
  value,
  language = "typescript",
  path,
  readOnly = false,
  theme = "dark",
  onChange
}) {
  const editorProps = path ? { path } : {};
  return /* @__PURE__ */ jsx("div", { className: "aura-monaco", children: /* @__PURE__ */ jsx(
    Editor,
    {
      value,
      language,
      ...editorProps,
      theme: theme === "light" ? "vs" : theme === "high-contrast" ? "hc-black" : "vs-dark",
      options: {
        readOnly,
        automaticLayout: true,
        minimap: { enabled: false },
        fontFamily: "var(--ag-font-mono)",
        fontSize: 13,
        renderLineHighlight: "line",
        scrollBeyondLastLine: false,
        tabSize: 2
      },
      onChange: (nextValue) => onChange?.(nextValue ?? "")
    }
  ) });
}
function AuraTimeline({ items }) {
  return /* @__PURE__ */ jsx("ol", { className: "aura-timeline", "aria-label": "Timeline", children: items.map((item) => /* @__PURE__ */ jsxs(
    "li",
    {
      className: cx(
        "aura-timeline__item",
        `aura-timeline__item--${item.severity ?? "neutral"}`
      ),
      children: [
        /* @__PURE__ */ jsx("span", { className: "aura-timeline__dot" }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("div", { className: "aura-timeline__title", children: item.title }),
          /* @__PURE__ */ jsx("time", { className: "aura-timeline__time", dateTime: item.timestamp, children: item.timestamp }),
          item.description ? /* @__PURE__ */ jsx("p", { children: item.description }) : null
        ] })
      ]
    },
    item.id
  )) });
}
function AuraInspector({
  title,
  children
}) {
  return /* @__PURE__ */ jsxs("section", { className: "aura-panel", "aria-label": title, children: [
    /* @__PURE__ */ jsxs("header", { className: "aura-panel__header", children: [
      /* @__PURE__ */ jsx(Info, { size: 15 }),
      /* @__PURE__ */ jsx("h2", { children: title })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "aura-panel__body", children })
  ] });
}
function AuraCommandPalette({
  registry,
  open,
  onOpenChange
}) {
  const [query, setQuery] = useState("");
  const commands = registry.find(query);
  if (!open) {
    return null;
  }
  const grouped = commands.reduce(
    (groups, command) => {
      const groupCommands = groups.get(command.group) ?? [];
      groupCommands.push(command);
      groups.set(command.group, groupCommands);
      return groups;
    },
    /* @__PURE__ */ new Map()
  );
  const runCommand = async (command) => {
    await registry.run(command.id);
    onOpenChange(false);
    setQuery("");
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "aura-modal-backdrop",
      role: "presentation",
      onMouseDown: () => onOpenChange(false),
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          className: "aura-command-palette",
          role: "dialog",
          "aria-modal": "true",
          "aria-label": "Command palette",
          onMouseDown: (event) => event.stopPropagation(),
          children: [
            /* @__PURE__ */ jsxs("label", { className: "aura-command-palette__search", children: [
              /* @__PURE__ */ jsx(Search, { size: 16 }),
              /* @__PURE__ */ jsx(
                "input",
                {
                  autoFocus: true,
                  value: query,
                  placeholder: "Search commands",
                  onChange: (event) => setQuery(event.currentTarget.value)
                }
              )
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "aura-command-palette__results", children: [
              Array.from(grouped.entries()).map(([group, groupCommands]) => /* @__PURE__ */ jsxs("div", { className: "aura-command-palette__group", children: [
                /* @__PURE__ */ jsx("div", { className: "aura-command-palette__group-title", children: group }),
                groupCommands.map((command) => /* @__PURE__ */ jsxs(
                  "button",
                  {
                    type: "button",
                    disabled: command.disabled,
                    className: "aura-command-palette__item",
                    onClick: () => void runCommand(command),
                    children: [
                      /* @__PURE__ */ jsx("span", { children: command.title }),
                      command.keybinding ? /* @__PURE__ */ jsx("kbd", { children: command.keybinding }) : null
                    ]
                  },
                  command.id
                ))
              ] }, group)),
              !commands.length ? /* @__PURE__ */ jsx(
                AuraEmptyState,
                {
                  title: "No commands",
                  description: "Refine the query or register commands through the platform registry."
                }
              ) : null
            ] })
          ]
        }
      )
    }
  );
}
function AuraStatusBar({
  items
}) {
  return /* @__PURE__ */ jsx("div", { className: "aura-status-items", role: "status", children: items.map((item) => /* @__PURE__ */ jsxs(
    "span",
    {
      className: cx(
        "aura-status-item",
        `aura-status-item--${item.tone ?? "neutral"}`
      ),
      children: [
        item.label,
        item.value ? /* @__PURE__ */ jsx("strong", { children: item.value }) : null
      ]
    },
    item.id
  )) });
}
function AuraProblemsPanel({ problems }) {
  const icon = {
    info: /* @__PURE__ */ jsx(Info, { size: 14 }),
    warning: /* @__PURE__ */ jsx(AlertTriangle, { size: 14 }),
    error: /* @__PURE__ */ jsx(ShieldAlert, { size: 14 })
  };
  return /* @__PURE__ */ jsxs("section", { className: "aura-panel", "aria-label": "Problems", children: [
    /* @__PURE__ */ jsxs("header", { className: "aura-panel__header", children: [
      /* @__PURE__ */ jsx(AlertTriangle, { size: 15 }),
      /* @__PURE__ */ jsx("h2", { children: "Problems" }),
      /* @__PURE__ */ jsx("span", { className: "aura-panel__count", children: problems.length })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "aura-problems", children: [
      problems.map((problem) => /* @__PURE__ */ jsxs(
        "div",
        {
          className: cx("aura-problem", `aura-problem--${problem.severity}`),
          children: [
            icon[problem.severity],
            /* @__PURE__ */ jsx("span", { children: problem.message }),
            /* @__PURE__ */ jsx("code", { children: [problem.path, problem.line, problem.column].filter(Boolean).join(":") })
          ]
        },
        problem.id
      )),
      !problems.length ? /* @__PURE__ */ jsx(
        AuraEmptyState,
        {
          title: "No problems",
          description: "Project checks are clean.",
          compact: true
        }
      ) : null
    ] })
  ] });
}
function AuraSettingsPanel({
  productName,
  telemetryEnabled,
  crashReportsEnabled,
  onTelemetryChange,
  onCrashReportsChange
}) {
  const { mode, setMode } = useContext2(AuraThemeContext);
  return /* @__PURE__ */ jsxs("section", { className: "aura-panel", "aria-label": `${productName} settings`, children: [
    /* @__PURE__ */ jsxs("header", { className: "aura-panel__header", children: [
      /* @__PURE__ */ jsx(Settings, { size: 15 }),
      /* @__PURE__ */ jsx("h2", { children: "Settings" })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "aura-settings", children: [
      /* @__PURE__ */ jsxs("label", { children: [
        /* @__PURE__ */ jsx("span", { children: "Theme" }),
        /* @__PURE__ */ jsxs(
          "select",
          {
            value: mode,
            onChange: (event) => setMode(event.currentTarget.value),
            children: [
              /* @__PURE__ */ jsx("option", { value: "system", children: "Follow OS" }),
              /* @__PURE__ */ jsx("option", { value: "dark", children: "Dark" }),
              /* @__PURE__ */ jsx("option", { value: "light", children: "Light" }),
              /* @__PURE__ */ jsx("option", { value: "high-contrast", children: "High contrast" })
            ]
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("label", { children: [
        /* @__PURE__ */ jsx("span", { children: "Telemetry" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "checkbox",
            checked: telemetryEnabled,
            onChange: (event) => onTelemetryChange(event.currentTarget.checked)
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("label", { children: [
        /* @__PURE__ */ jsx("span", { children: "Crash reports" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "checkbox",
            checked: crashReportsEnabled,
            onChange: (event) => onCrashReportsChange(event.currentTarget.checked)
          }
        )
      ] })
    ] })
  ] });
}
function AuraWelcomePrivacyWizard({
  productWorkNoun = "rubrics, datasets, or agent traces",
  telemetryEnabled,
  crashReportsEnabled,
  onTelemetryChange,
  onCrashReportsChange,
  onComplete
}) {
  return /* @__PURE__ */ jsxs(
    "section",
    {
      className: "aura-privacy-wizard",
      "aria-label": "Welcome privacy choices",
      children: [
        /* @__PURE__ */ jsxs("article", { children: [
          /* @__PURE__ */ jsx("h2", { children: "Help improve AuraOne Open Studio" }),
          /* @__PURE__ */ jsx("p", { children: "If you opt in, the app will send us anonymous usage signals so we can prioritize what to build next. We will never send the contents of your work." }),
          /* @__PURE__ */ jsx("strong", { children: "What we send" }),
          /* @__PURE__ */ jsxs("ul", { children: [
            /* @__PURE__ */ jsx("li", { children: "Anonymous install ID" }),
            /* @__PURE__ */ jsx("li", { children: "App version and OS" }),
            /* @__PURE__ */ jsx("li", { children: "High-level feature usage" }),
            /* @__PURE__ */ jsx("li", { children: "Error counts" }),
            /* @__PURE__ */ jsx("li", { children: "Session length" })
          ] }),
          /* @__PURE__ */ jsx("strong", { children: "What we never send" }),
          /* @__PURE__ */ jsxs("ul", { children: [
            /* @__PURE__ */ jsxs("li", { children: [
              "Your ",
              productWorkNoun
            ] }),
            /* @__PURE__ */ jsx("li", { children: "API keys" }),
            /* @__PURE__ */ jsx("li", { children: "File paths or hostnames" }),
            /* @__PURE__ */ jsx("li", { children: "Anything you typed into the editor" })
          ] }),
          /* @__PURE__ */ jsxs("label", { children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "checkbox",
                checked: telemetryEnabled,
                onChange: (event) => onTelemetryChange(event.currentTarget.checked)
              }
            ),
            /* @__PURE__ */ jsx("span", { children: "Help improve AuraOne Open Studio (opt in)" })
          ] }),
          /* @__PURE__ */ jsx("p", { children: "Don't send anything (default)" })
        ] }),
        /* @__PURE__ */ jsxs("article", { children: [
          /* @__PURE__ */ jsx("h2", { children: "Help us fix what breaks" }),
          /* @__PURE__ */ jsx("p", { children: "If something crashes, we'd love to know so we can fix it. We can send an anonymous crash report to AuraOne if you opt in." }),
          /* @__PURE__ */ jsx("strong", { children: "What we send" }),
          /* @__PURE__ */ jsxs("ul", { children: [
            /* @__PURE__ */ jsx("li", { children: "The stack trace and minidump from the crash" }),
            /* @__PURE__ */ jsx("li", { children: "Your OS and app version" }),
            /* @__PURE__ */ jsx("li", { children: "An install-scoped random ID" })
          ] }),
          /* @__PURE__ */ jsx("strong", { children: "What we never send" }),
          /* @__PURE__ */ jsxs("ul", { children: [
            /* @__PURE__ */ jsxs("li", { children: [
              "The contents of your ",
              productWorkNoun
            ] }),
            /* @__PURE__ */ jsx("li", { children: "API keys" }),
            /* @__PURE__ */ jsx("li", { children: "File paths or hostnames" })
          ] }),
          /* @__PURE__ */ jsxs("label", { children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "checkbox",
                checked: crashReportsEnabled,
                onChange: (event) => onCrashReportsChange(event.currentTarget.checked)
              }
            ),
            /* @__PURE__ */ jsx("span", { children: "Send crash reports" })
          ] }),
          /* @__PURE__ */ jsx("p", { children: "Don't send anything" })
        ] }),
        /* @__PURE__ */ jsx("button", { type: "button", onClick: onComplete, children: "Continue" })
      ]
    }
  );
}
function AuraUpdatePrompt({
  version,
  releaseNotes,
  signedBy,
  mandatory = false,
  onInstallNow,
  onInstallOnRestart,
  onRemindLater
}) {
  return /* @__PURE__ */ jsxs("section", { className: "aura-update-prompt", "aria-label": "Software update", children: [
    /* @__PURE__ */ jsxs("header", { children: [
      /* @__PURE__ */ jsx(CheckCircle2, { size: 18 }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("h2", { children: "Update available" }),
        /* @__PURE__ */ jsxs("p", { children: [
          "Version ",
          version
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx("pre", { children: releaseNotes }),
    /* @__PURE__ */ jsxs("p", { title: `Signed by ${signedBy}`, children: [
      "Signed by ",
      signedBy
    ] }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("button", { type: "button", onClick: onInstallOnRestart, children: "Install next launch" }),
      /* @__PURE__ */ jsx("button", { type: "button", onClick: onInstallNow, children: "Install now" }),
      /* @__PURE__ */ jsx("button", { type: "button", onClick: onRemindLater, disabled: mandatory, children: "Remind later" })
    ] })
  ] });
}
function AuraIntakeIdentityFields({
  displayName,
  email = "",
  intent,
  onDisplayNameChange,
  onEmailChange,
  onIntentChange
}) {
  return /* @__PURE__ */ jsxs("section", { className: "aura-intake-identity", "aria-label": "Intake identity", children: [
    /* @__PURE__ */ jsxs("label", { children: [
      /* @__PURE__ */ jsx("span", { children: "Display name" }),
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "text",
          value: displayName,
          required: true,
          autoComplete: "name",
          onChange: (event) => onDisplayNameChange(event.currentTarget.value)
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("label", { children: [
      /* @__PURE__ */ jsx("span", { children: "Email (optional)" }),
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "email",
          value: email,
          autoComplete: "email",
          onChange: (event) => onEmailChange(event.currentTarget.value)
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("label", { children: [
      /* @__PURE__ */ jsx("span", { children: "Intent note" }),
      /* @__PURE__ */ jsx(
        "textarea",
        {
          value: intent,
          required: true,
          rows: 4,
          onChange: (event) => onIntentChange(event.currentTarget.value)
        }
      )
    ] })
  ] });
}
function AuraKeychainFallbackWarning({
  backendKind,
  firstSecretSet = false,
  onDismiss
}) {
  if (backendKind !== "linux-encrypted-file-fallback" || !firstSecretSet) {
    return null;
  }
  return /* @__PURE__ */ jsxs(
    "section",
    {
      className: "aura-keychain-warning",
      role: "status",
      "aria-label": "Linux keychain fallback warning",
      children: [
        /* @__PURE__ */ jsx(ShieldAlert, { size: 18 }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("strong", { children: "Linux Secret Service is unavailable" }),
          /* @__PURE__ */ jsx("p", { children: "AuraOne stored this secret in the encrypted local fallback store. Install or unlock a Secret Service provider to use the native Linux keychain." })
        ] }),
        onDismiss ? /* @__PURE__ */ jsx("button", { type: "button", onClick: onDismiss, children: "Dismiss" }) : null
      ]
    }
  );
}
var ToastContext = createContext2({
  push: () => void 0,
  dismiss: () => void 0
});
function AuraToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const value = useMemo(
    () => ({
      push(toast) {
        setToasts((current) => [
          ...current,
          { ...toast, id: crypto.randomUUID() }
        ]);
      },
      dismiss(id) {
        setToasts((current) => current.filter((toast) => toast.id !== id));
      }
    }),
    []
  );
  return /* @__PURE__ */ jsxs(ToastContext.Provider, { value, children: [
    children,
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "aura-toast-region",
        "aria-live": "polite",
        "aria-label": "Notifications",
        children: toasts.map((toast) => /* @__PURE__ */ jsxs(
          "div",
          {
            className: cx("aura-toast", `aura-toast--${toast.tone ?? "info"}`),
            children: [
              /* @__PURE__ */ jsx(Bell, { size: 15 }),
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("strong", { children: toast.title }),
                toast.description ? /* @__PURE__ */ jsx("p", { children: toast.description }) : null
              ] }),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  "aria-label": "Dismiss notification",
                  onClick: () => value.dismiss(toast.id),
                  children: /* @__PURE__ */ jsx(X, { size: 14 })
                }
              )
            ]
          },
          toast.id
        ))
      }
    )
  ] });
}
function useAuraToast() {
  return useContext2(ToastContext);
}
function AuraModal({
  open,
  title,
  children,
  onClose
}) {
  if (!open) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "aura-modal-backdrop",
      role: "presentation",
      onMouseDown: onClose,
      children: /* @__PURE__ */ jsxs(
        "section",
        {
          className: "aura-modal",
          role: "dialog",
          "aria-modal": "true",
          "aria-label": title,
          onMouseDown: (event) => event.stopPropagation(),
          children: [
            /* @__PURE__ */ jsxs("header", { className: "aura-modal__header", children: [
              /* @__PURE__ */ jsx("h2", { children: title }),
              /* @__PURE__ */ jsx("button", { type: "button", "aria-label": "Close modal", onClick: onClose, children: /* @__PURE__ */ jsx(X, { size: 16 }) })
            ] }),
            /* @__PURE__ */ jsx("div", { className: "aura-modal__body", children })
          ]
        }
      )
    }
  );
}
function AuraEmptyState({
  title,
  description,
  compact = false
}) {
  return /* @__PURE__ */ jsxs("div", { className: cx("aura-state", compact && "aura-state--compact"), children: [
    /* @__PURE__ */ jsx(Code2, { size: compact ? 18 : 28 }),
    /* @__PURE__ */ jsx("strong", { children: title }),
    description ? /* @__PURE__ */ jsx("p", { children: description }) : null
  ] });
}
function AuraLoadingState({ label = "Loading" }) {
  return /* @__PURE__ */ jsxs("div", { className: "aura-state", role: "status", children: [
    /* @__PURE__ */ jsx(Loader2, { className: "aura-spin", size: 28 }),
    /* @__PURE__ */ jsx("strong", { children: label })
  ] });
}
function AuraErrorState({
  title,
  description,
  onRetry
}) {
  return /* @__PURE__ */ jsxs("div", { className: "aura-state aura-state--error", role: "alert", children: [
    /* @__PURE__ */ jsx(ShieldAlert, { size: 28 }),
    /* @__PURE__ */ jsx("strong", { children: title }),
    description ? /* @__PURE__ */ jsx("p", { children: description }) : null,
    onRetry ? /* @__PURE__ */ jsx("button", { type: "button", onClick: onRetry, children: "Retry" }) : null
  ] });
}
function AuraTelemetryEventLog({
  events
}) {
  return /* @__PURE__ */ jsxs("section", { className: "aura-panel", "aria-label": "Telemetry event log", children: [
    /* @__PURE__ */ jsxs("header", { className: "aura-panel__header", children: [
      /* @__PURE__ */ jsx(CheckCircle2, { size: 15 }),
      /* @__PURE__ */ jsx("h2", { children: "Telemetry Event Log" })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "aura-event-log", children: events.length ? events.map((event) => /* @__PURE__ */ jsxs("article", { className: "aura-event-log__row", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("strong", { children: event.name }),
        /* @__PURE__ */ jsx("time", { dateTime: event.timestamp, children: event.timestamp })
      ] }),
      /* @__PURE__ */ jsx("span", { children: event.deliveryStatus === "local_preview" ? "local preview" : event.deliveryStatus === "would_send" ? "not sent" : event.destination === "local" ? "local only" : "delivery unverified" }),
      /* @__PURE__ */ jsx(
        "pre",
        {
          tabIndex: 0,
          role: "region",
          "aria-label": `${event.name} payload preview`,
          children: JSON.stringify(event.payloadPreview, null, 2)
        }
      )
    ] }, event.id)) : /* @__PURE__ */ jsx(
      AuraEmptyState,
      {
        compact: true,
        title: "No local events",
        description: "Eligible diagnostics appear here after they are recorded. This log does not confirm network delivery."
      }
    ) })
  ] });
}
var AURA_INTAKE_PRIVACY_URL = "https://auraone.ai/open/privacy/intake";
var AURA_INTAKE_PRIVACY_COPY = {
  sent: [
    "A manifest describing the project metadata you entered.",
    "The payload files you reviewed in the preview screen above.",
    "A signature from your local install (used only for deduplication)."
  ],
  neverSent: [
    "Your API keys.",
    "File paths from your machine (replaced with <PROJECT>/... references).",
    "Hostnames or environment variables.",
    "Anything you did not see in the preview screen above."
  ],
  consent: "If you cancel before clicking Send, nothing leaves your machine. If you click Send, the only thing that leaves your machine is the contents of the packet you just reviewed."
};
function AuraIntakePacketPreview({
  packet,
  manifestTree,
  consentChecked = false,
  onConsentChange,
  privacyUrl = AURA_INTAKE_PRIVACY_URL
}) {
  const totalBytes = packet.includedFiles.reduce(
    (sum, file) => sum + file.bytes,
    0
  );
  return /* @__PURE__ */ jsxs("section", { className: "aura-panel", "aria-label": "Intake packet preview", children: [
    /* @__PURE__ */ jsxs("header", { className: "aura-panel__header", children: [
      /* @__PURE__ */ jsx(FolderOpen, { size: 15 }),
      /* @__PURE__ */ jsx("h2", { children: "Intake Packet" })
    ] }),
    /* @__PURE__ */ jsxs("dl", { className: "aura-packet-summary", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("dt", { children: "Packet" }),
        /* @__PURE__ */ jsx("dd", { children: packet.packetId })
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("dt", { children: "Flagship" }),
        /* @__PURE__ */ jsx("dd", { children: packet.flagship })
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("dt", { children: "Schema" }),
        /* @__PURE__ */ jsx("dd", { children: packet.schemaVersion })
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("dt", { children: "Files" }),
        /* @__PURE__ */ jsxs("dd", { children: [
          packet.includedFiles.length,
          " /",
          " ",
          new Intl.NumberFormat("en", { notation: "compact" }).format(
            totalBytes
          ),
          "B"
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "aura-packet-files", children: packet.includedFiles.map((file) => /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("code", { children: file.path }),
      /* @__PURE__ */ jsx("span", { children: file.role })
    ] }, file.path)) }),
    /* @__PURE__ */ jsxs("div", { className: "aura-packet-exclusions", children: [
      /* @__PURE__ */ jsx("strong", { children: "Excluded" }),
      packet.excludedPatterns.map((pattern) => /* @__PURE__ */ jsx("code", { children: pattern }, pattern))
    ] }),
    packet.warnings.length ? /* @__PURE__ */ jsx("div", { className: "aura-packet-warnings", children: packet.warnings.map((warning) => /* @__PURE__ */ jsx("p", { children: warning }, warning)) }) : null,
    manifestTree ? /* @__PURE__ */ jsxs("section", { className: "aura-packet-manifest", "aria-label": "Manifest tree", children: [
      /* @__PURE__ */ jsx("h3", { children: "Manifest Tree" }),
      /* @__PURE__ */ jsx("pre", { children: JSON.stringify(manifestTree, null, 2) })
    ] }) : null,
    onConsentChange ? /* @__PURE__ */ jsxs(
      "section",
      {
        className: "aura-packet-privacy",
        "aria-label": "Intake privacy review",
        children: [
          /* @__PURE__ */ jsx("h3", { children: "What is sent in an intake packet?" }),
          /* @__PURE__ */ jsx("p", { children: "A single zip file containing:" }),
          /* @__PURE__ */ jsx("ul", { children: AURA_INTAKE_PRIVACY_COPY.sent.map((item) => /* @__PURE__ */ jsx("li", { children: item }, item)) }),
          /* @__PURE__ */ jsx("h3", { children: "What is never sent in an intake packet?" }),
          /* @__PURE__ */ jsx("ul", { children: AURA_INTAKE_PRIVACY_COPY.neverSent.map((item) => /* @__PURE__ */ jsx("li", { children: item }, item)) }),
          /* @__PURE__ */ jsx("p", { children: AURA_INTAKE_PRIVACY_COPY.consent }),
          /* @__PURE__ */ jsx("a", { href: privacyUrl, children: "Read the intake privacy policy" }),
          /* @__PURE__ */ jsxs("label", { className: "aura-packet-consent", children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "checkbox",
                required: true,
                checked: consentChecked,
                onChange: (event) => onConsentChange(event.currentTarget.checked)
              }
            ),
            /* @__PURE__ */ jsx("span", { children: "I reviewed this packet and consent to send only the contents shown above." })
          ] })
        ]
      }
    ) : null
  ] });
}
function AuraFileWatcherStatus({
  state,
  watchedPath,
  eventsQueued = 0
}) {
  const tone = state === "connected" ? "success" : state === "polling" ? "warning" : "neutral";
  return /* @__PURE__ */ jsxs(
    "section",
    {
      className: cx("aura-file-watcher", `aura-file-watcher--${tone}`),
      "aria-live": "polite",
      "aria-label": "File watcher status",
      children: [
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(Bell, { size: 15 }),
          /* @__PURE__ */ jsx("strong", { children: state === "connected" ? "Watching" : state === "polling" ? "Polling" : "Watcher disabled" })
        ] }),
        watchedPath ? /* @__PURE__ */ jsx("code", { children: watchedPath }) : null,
        /* @__PURE__ */ jsxs("span", { children: [
          eventsQueued,
          " queued"
        ] })
      ]
    }
  );
}
function AuraWelcomeWindow({
  productName,
  recentProjects,
  onOpenFolder,
  onOpenRecent
}) {
  return /* @__PURE__ */ jsxs("section", { className: "aura-welcome", "aria-label": `${productName} welcome`, children: [
    /* @__PURE__ */ jsxs("header", { children: [
      /* @__PURE__ */ jsx(Code2, { size: 22 }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("h1", { children: productName }),
        /* @__PURE__ */ jsx("p", { children: "Local-first project workspace" })
      ] })
    ] }),
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        className: "aura-welcome__primary",
        onClick: onOpenFolder,
        children: [
          /* @__PURE__ */ jsx(FolderOpen, { size: 16 }),
          /* @__PURE__ */ jsx("span", { children: "Open Folder" })
        ]
      }
    ),
    recentProjects.length ? /* @__PURE__ */ jsx("nav", { "aria-label": "Recent projects", children: recentProjects.map((project) => /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: () => onOpenRecent(project.path),
        children: [
          /* @__PURE__ */ jsx(Folder, { size: 15 }),
          /* @__PURE__ */ jsx("span", { children: project.name }),
          /* @__PURE__ */ jsx("code", { children: project.path })
        ]
      },
      project.path
    )) }) : null
  ] });
}
function AuraPanel({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx("section", { className: cx("aura-panel", className), ...props, children });
}
function createDefaultIdeCommands(openFolder, openSettings) {
  return createCommandRegistry([
    {
      id: "project.open-folder",
      title: "Open Folder",
      group: "Project",
      keybinding: "Mod+O",
      handler: openFolder
    },
    {
      id: "app.settings",
      title: "Open Settings",
      group: "Application",
      keybinding: "Mod+,",
      handler: openSettings
    }
  ]);
}
function isCommandPaletteKey(event) {
  return (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
}

// src/ssr-posture.ts
var AURA_IDE_COMPONENT_POSTURE = [
  {
    component: "AuraIdeAppFrame",
    posture: "client-only",
    rationale: "Registers keyboard listeners for the command palette."
  },
  {
    component: "AuraSplitPane",
    posture: "client-only",
    rationale: "Uses pointer and keyboard resizing state."
  },
  {
    component: "AuraProjectTree",
    posture: "client-only",
    rationale: "Owns expandable tree state."
  },
  {
    component: "AuraMonaco",
    posture: "client-only-with-suspense",
    rationale: "Wraps Monaco editor, which is browser-only."
  },
  {
    component: "AuraTimeline",
    posture: "ssr-safe",
    rationale: "Static list rendering when handlers are not attached."
  },
  {
    component: "AuraInspector",
    posture: "ssr-safe",
    rationale: "Static panel rendering."
  },
  {
    component: "AuraCommandPalette",
    posture: "client-only",
    rationale: "Interactive modal search and command execution."
  },
  {
    component: "AuraStatusBar",
    posture: "ssr-safe",
    rationale: "Static status item rendering."
  },
  {
    component: "AuraProblemsPanel",
    posture: "ssr-safe",
    rationale: "Static diagnostic list rendering."
  },
  {
    component: "AuraSettingsPanel",
    posture: "client-only",
    rationale: "Interactive privacy toggles."
  },
  {
    component: "AuraWelcomePrivacyWizard",
    posture: "client-only",
    rationale: "Interactive telemetry and crash opt-in choices."
  },
  {
    component: "AuraUpdatePrompt",
    posture: "client-only",
    rationale: "Interactive install/remind update actions."
  },
  {
    component: "AuraIntakeIdentityFields",
    posture: "client-only",
    rationale: "Controlled intake contact and intent form inputs."
  },
  {
    component: "AuraKeychainFallbackWarning",
    posture: "client-only",
    rationale: "Dismissible first-use Linux keychain fallback warning."
  },
  {
    component: "AuraToastProvider",
    posture: "client-only",
    rationale: "Owns transient notification state."
  },
  {
    component: "AuraModal",
    posture: "client-only",
    rationale: "Interactive dialog state and dismissal."
  },
  {
    component: "AuraEmptyState",
    posture: "ssr-safe",
    rationale: "Static empty-state rendering."
  },
  {
    component: "AuraLoadingState",
    posture: "ssr-safe",
    rationale: "Static loading-state rendering."
  },
  {
    component: "AuraErrorState",
    posture: "ssr-safe",
    rationale: "Static error-state rendering."
  },
  {
    component: "AuraTelemetryEventLog",
    posture: "ssr-safe",
    rationale: "Static event-list rendering when data is supplied."
  },
  {
    component: "AuraIntakePacketPreview",
    posture: "ssr-safe",
    rationale: "Static packet preview rendering."
  },
  {
    component: "AuraFileWatcherStatus",
    posture: "ssr-safe",
    rationale: "Static watcher status rendering when data is supplied."
  },
  {
    component: "AuraWelcomeWindow",
    posture: "client-only",
    rationale: "Interactive open-folder and recent-project actions."
  },
  {
    component: "AuraTabbedShell",
    posture: "client-only",
    rationale: "Interactive tab selection."
  }
];
function postureFor(component) {
  return AURA_IDE_COMPONENT_POSTURE.find(
    (entry) => entry.component === component
  );
}

// src/index.ts
import {
  ProoflineButton,
  ProoflineProductGlyph,
  ProoflinePanel,
  ProoflineState,
  ProoflineStatus,
  AuraOneMark,
  installOfficialStyleSheet,
  prooflineProductIcons,
  prooflineStatusLabels,
  prooflineStatusTones
} from "@auraone/proofline-oss";
export {
  AURA_IDE_COMPONENT_POSTURE,
  AURA_INTAKE_PRIVACY_COPY,
  AURA_INTAKE_PRIVACY_URL,
  AuraCommandPalette,
  AuraEmptyState,
  AuraErrorState,
  AuraFileWatcherStatus,
  AuraIdeAppFrame,
  AuraInspector,
  AuraIntakeIdentityFields,
  AuraIntakePacketPreview,
  AuraKeychainFallbackWarning,
  AuraLoadingState,
  AuraModal,
  AuraMonaco,
  AuraOneMark,
  AuraPanel,
  AuraProblemsPanel,
  AuraProjectTree,
  AuraSettingsPanel,
  AuraSplitPane,
  AuraStatusBar,
  AuraTabbedShell,
  AuraTelemetryEventLog,
  AuraThemeContext,
  AuraTimeline,
  AuraToastProvider,
  AuraUpdatePrompt,
  AuraWelcomePrivacyWizard,
  AuraWelcomeWindow,
  ProoflineButton,
  ProoflinePanel,
  ProoflineProductGlyph,
  ProoflineState,
  ProoflineStatus,
  createCommandRegistry,
  createDefaultIdeCommands,
  installOfficialStyleSheet,
  isCommandPaletteKey,
  postureFor,
  prooflineProductIcons,
  prooflineStatusLabels,
  prooflineStatusTones,
  useAuraTheme,
  useAuraToast
};
//# sourceMappingURL=index.js.map